"use client";

import * as React from "react";
import { cn } from "../../lib/utils";
import { formatCompactTimeAgo, formatCompactTimeRemaining, addDays } from "@artha/utils";
import LocationSmIcon from "../icons/locationSmIcon";
import ShareIcon from "../icons/shareIcon";
import { InfoTooltip } from "./info-tooltip";

const DEFAULT_EXPIRY_DAYS = 30;
const DEFAULT_ACCENT_COLOR = "#0D41E1";
const DEFAULT_FOOTER_LABEL = "EPA";
const DEFAULT_REMOTE_LABEL = "Remote";
const FOOTER_VALUE_CLASS =
  "min-w-0 whitespace-normal break-words text-[22px] font-bold leading-none [overflow-wrap:anywhere]";
const FOOTER_CONTAINER_CLASS = "min-w-0 flex flex-1 items-end gap-1.5 text-[#344054]";

/**
 * Job data structure matching JobItemDto from @artha/api-contracts
 */
export interface JobData {
  id: string;
  title: string;
  company: string;
  location?: string;
  description?: string;
  logo?: string | null;
  posted_date: string;
  job_type?: string | null;
  salary_min?: number | null;
  salary_max?: number | null;
  salary_curr?: string | null;
  cpc_value?: number | null;
  cpa_value?: number | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  apply_url?: string;
  similarityProof?: {
    title: string;
    clicks: number;
  } | null;
}

export interface JobCardProps {
  /** The job object - primary way to pass job data */
  job: JobData;
  /** Hide the posted/expiry header row for contexts like Post Studio */
  hideHeader?: boolean;
  /** Optional selected state for flows like Post Studio */
  selected?: boolean;
  /** Optional insight lines rendered below the company row */
  insights?: string[];
  /** Optional metric badges rendered above the footer */
  badges?: Array<{ label: string; value: string }>;
  /** Override posted time text in the card header */
  postedLabel?: string;
  /** Override expiry text in the card header */
  expiryLabel?: string;
  /** Hide expiry text in the card header */
  hideExpiryLabel?: boolean;
  /** Called when share button is clicked, receives the job */
  onShare?: (job: JobData) => void;
  /** Called when card is clicked/selected, receives the job */
  onSelect?: (job: JobData) => void;
  /** Additional class names */
  className?: string;
  /** Days until expiry (default: 30) */
  expiryDays?: number;
  /** Custom location icon */
  locationIcon?: React.ReactNode;
  /** Custom share icon */
  shareIcon?: React.ReactNode;
  /** Accent color for EPA and share button */
  accentColor?: string;
  /** Footer label shown before the highlighted value */
  footerLabel?: string;
  /** Override footer value instead of using job CPA/CPC */
  footerValue?: string;
  /** Translation: suffix for time ago (e.g., "ago") */
  timeAgoSuffix?: string;
  /** Translation: remote location label */
  locationRemote?: string;
  /** Custom earning text to override the default RPA/RPC */
  earningText?: React.ReactNode;
}

function JobCard({
  job,
  hideHeader = false,
  selected = false,
  insights,
  badges,
  postedLabel,
  expiryLabel,
  hideExpiryLabel = false,
  onShare,
  onSelect,
  className,
  expiryDays = DEFAULT_EXPIRY_DAYS,
  locationIcon,
  shareIcon,
  accentColor = DEFAULT_ACCENT_COLOR,
  footerLabel = DEFAULT_FOOTER_LABEL,
  footerValue,
  timeAgoSuffix = "",
  locationRemote = DEFAULT_REMOTE_LABEL,
  earningText,
}: JobCardProps) {
  const isSelectable = typeof onSelect === "function";

  /** Builds the short posted-time label shown in the header. */
  const postedAgo = React.useMemo(() => {
    const formatted = formatCompactTimeAgo(job.posted_date || new Date().toISOString());
    return timeAgoSuffix ? `${formatted} ${timeAgoSuffix}` : formatted;
  }, [job.posted_date, timeAgoSuffix]);

  /** Builds the compact expiry countdown shown in the header when enabled. */
  const expiryDate = React.useMemo(() => {
    const expiryDateValue = addDays(job.posted_date || new Date().toISOString(), expiryDays);
    return formatCompactTimeRemaining(expiryDateValue);
  }, [job.posted_date, expiryDays]);

  /** Chooses the default payout metric when callers do not pass a custom footer value. */
  const rawEpaValue = job.cpa_value ?? job.cpc_value ?? null;
  const epaValue = rawEpaValue != null ? Number(rawEpaValue).toFixed(2) : null;
  const epaLabel = job.cpa_value != null ? "RPA" : job.cpc_value != null ? "RPC" : null;

  /** Builds the display location from the normalized city/state/country fields. */
  const displayLocation = React.useMemo(() => {
    const parts = [job.city, job.state, job.country].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : job.location || locationRemote;
  }, [job.city, job.state, job.country, job.location, locationRemote]);

  /** Keeps the share button from also triggering card selection. */
  const handleShare = (event: React.MouseEvent) => {
    event.stopPropagation();
    onShare?.(job);
  };

  const handleSelect = () => {
    onSelect?.(job);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!isSelectable) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(job);
    }
  };

  /** Renders the footer value row for custom earnings or fallback payout metrics. */
  const renderFooterMetric = () => {
    if (earningText) {
      return (
        <div className="min-w-0 flex max-w-[80%] items-center">
          <span className="min-w-0 whitespace-normal break-words text-sm font-semibold leading-tight text-[#344054] [overflow-wrap:anywhere]">
            {earningText}
          </span>
        </div>
      );
    }

    if (footerValue) {
      return (
        <div className={FOOTER_CONTAINER_CLASS}>
          <span className="shrink-0 text-sm font-medium">{footerLabel}:</span>
          <span className={FOOTER_VALUE_CLASS}>{footerValue}</span>
        </div>
      );
    }
    if (epaValue != null) {
      return (
        <div className={cn(FOOTER_CONTAINER_CLASS, "pointer-events-auto")}>
          <span className="shrink-0 text-sm font-medium flex items-center leading-none">
            <span>{epaLabel}</span>
            <span>:</span>
          </span>
          <span className={cn(FOOTER_VALUE_CLASS, "leading-none")}>${epaValue}</span>
        </div>
      );
    }

    return <div />;
  };

  return (
    <article
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      role={isSelectable ? "button" : undefined}
      tabIndex={isSelectable ? 0 : undefined}
      className={cn(
        "w-full rounded-lg border bg-white p-3 flex flex-col gap-3.5",
        selected
          ? "border-[#ED5AA9] bg-white"
          : "border-black bg-gradient-to-r from-[#FCFEFF] to-[#FCFEFF]",
        isSelectable && "cursor-pointer",
        className
      )}
    >
      {
        !hideHeader ? (
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-1.5">
              <span className="text-[#667085] text-[10px] font-normal ">
                {postedLabel ?? postedAgo}
              </span>
              {!hideExpiryLabel ? (
                <>
                  <div className="size-[3px] rounded-full bg-[#667085]" />
                  <span className="text-[#FC6E16] text-[10px] font-normal ">
                    {expiryLabel ?? expiryDate}
                  </span>
                </>
              ) : null}
            </div>
            {job.similarityProof && job.similarityProof.clicks > 0 && (
              <div className="flex items-center gap-1 text-[10px] font-bold text-[#ED5AA9]">
                <span>{job.similarityProof.clicks} audience clicks</span>
                <InfoTooltip
                  content={`Your audience clicked jobs similar to this ${job.similarityProof.clicks} times.`}
                  align="right"
                  position="bottom"
                  contentClassName="w-48 text-[11px]"
                />
              </div>
            )}
          </div>
        ) : null}

      {/* Company Info */}
      <div className="pointer-events-none relative z-20 flex items-start gap-2">
        <div className="shrink-0 size-10 rounded overflow-hidden">
          {job.logo ? (
            <img
              src={job.logo}
              alt={job.company}
              className="size-full object-cover"
            />
          ) : (
            <div className="size-full bg-gray-200 flex items-center justify-center">
              <span className="text-xs font-semibold text-gray-500">
                {job.company.charAt(0)}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 flex-1 min-w-0">
          <h3 className="text-[#344054] text-base font-semibold leading-tight">
            {job.title}
          </h3>
          <div className="flex items-center gap-1.5 w-full text-[#667085]">
            <span className="text-xs font-normal font-plus-jakarta-sans truncate">
              {job.company}
            </span>
            <div className="size-[3px] rounded-full bg-[#667085] shrink-0" />
            <div className="flex items-center w-1/2 gap-1">
              <div>
                {locationIcon || <LocationSmIcon />}
              </div>
              <span className="text-xs font-medium font-plus-jakarta-sans text-center truncate">
                {displayLocation}
              </span>
            </div>
          </div>
        </div>
      </div>

      {insights && insights.length > 0 ? (
        <div className="flex flex-col gap-1">
          {insights.map((insight, index) => (
            <p
              key={`${job.id}-insight-${index}`}
              className="text-xs font-medium leading-5 text-[#667085]"
            >
              {insight}
            </p>
          ))}
        </div>
      ) : null}

      {badges && badges.length > 0 ? (
        <div className="pointer-events-none relative z-20 flex w-full flex-wrap items-center gap-1.5">
          {badges.map((badge) => (
            <div
              key={`${job.id}-${badge.label}`}
              className="inline-flex items-center justify-center shrink-0 rounded-[4px] border-[0.5px] border-[#D1D1D6] bg-[#FBFBFB] px-2 py-[2px]"
            >
              <span className="text-[10px] font-medium leading-[18px] text-[#667085]">
                {badge.label}: {badge.value}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {/* Footer: EPA and Share */}
      <div className="pointer-events-none relative z-20 mt-auto flex items-center justify-between">
        {renderFooterMetric()}
        {onShare && (
          <button
            type="button"
            onClick={handleShare}
            className="pointer-events-auto relative z-30 flex items-center gap-1.5 rounded-lg hover:bg-gray-50 transition-colors"
            style={{ color: accentColor }}
          >
            <span className="text-base font-medium ">Share</span>
            <span className="text-lg">{shareIcon ?? <ShareIcon />}</span>
          </button>
        )}
      </div>
    </article>
  );
}

export { JobCard };
